import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const csaUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Class Creation",
  examWeight: "10–18%",
  focus: "The fundamental building blocks of Object-Oriented Programming (OOP)—designing classes, instantiating objects, and defining behaviors.",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: Abstraction and Program Design",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧱 The Concept of Abstraction</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Abstraction is the process of hiding complex implementation details and showing only the essential features of an object or algorithm to the user. It is one of the core pillars of Object-Oriented Programming (OOP).
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <p className="text-sm text-slate-600 italic"><strong>Real-World Analogy:</strong> When you drive a car, you use the steering wheel and pedals (the interface). You do not need to understand the complex thermodynamics of the internal combustion engine (the implementation) to drive.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>In Java:</strong> We use classes to create abstractions. A user interacts with a class via its <strong>public methods</strong> without needing to know how the code inside works.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ Types of Abstraction</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">📊 Data Abstraction</h4>
                <p className="text-sm text-slate-700 mt-1">Separates the properties of a data type from its concrete implementation.</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Attributes (Instance Variables):</strong> The specific data points defined in a class (e.g., name, gpa).</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">🛠️ Procedural Abstraction</h4>
                <p className="text-sm text-slate-700 mt-1">Hides the details of <em>how</em> a task is accomplished.</p>
                <p className="text-xs text-slate-600 mt-1"><strong>Behaviors (Methods):</strong> Actions like <code>calculateGPA()</code>; uses <strong>Method Decomposition</strong> to break large problems into smaller helper methods.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📐 Program Design (UML)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">Structure often represented in diagrams (Unified Modeling Language):</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-slate-700">
              <li><strong>Class Name:</strong> Noun (e.g., <code>Car</code>)</li>
              <li><strong>Attributes:</strong> Private data (e.g., <code>fuelLevel</code>)</li>
              <li><strong>Behaviors:</strong> Public actions (e.g., <code>drive()</code>, <code>refuel()</code>)</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Impact of Program Design",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Social and Ethical Implications</h3>
            <div className="space-y-4">
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <h4 className="font-bold text-emerald-900">🛡️ System Reliability</h4>
                <p className="text-sm text-slate-700 mt-1">Ensuring software works under stated conditions. Requires testing with <strong>valid inputs</strong>, <strong>invalid inputs</strong>, and <strong>edge cases</strong> (boundary values).</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-900">🛑 Bias in Computing</h4>
                <p className="text-sm text-slate-700 mt-1"><strong>Algorithmic Bias:</strong> Systems reflecting human biases. Often stems from incomplete or non-representative <strong>Data Sets</strong>.</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">📜 Intellectual Property (IP)</h4>
                <div className="grid md:grid-cols-2 gap-3 mt-2">
                  <div className="bg-white/50 p-2 rounded border border-amber-100">
                    <p className="text-xs font-bold text-amber-900">Open Source</p>
                    <p className="text-[10px] text-slate-600">Publicly modifiable and redistributable (e.g., Linux, Android).</p>
                  </div>
                  <div className="bg-white/50 p-2 rounded border border-amber-100">
                    <p className="text-xs font-bold text-amber-900">Proprietary</p>
                    <p className="text-[10px] text-slate-600">Owned by companies; users pay for licenses (e.g., Windows, macOS).</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Anatomy of a Class",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧬 Class Structure and Encapsulation</h3>
            <div className="space-y-3 mb-4">
              <KeyTerm term="public" definition="Code is accessible from any other class." />
              <KeyTerm term="private" definition="Code is accessible ONLY within the class where it is defined." />
              <KeyTerm term="class" definition="Defines a new reference type (blueprint)." />
            </div>
            <ImportantNote>
              <strong>Encapsulation:</strong> The bundling of data and methods and restricting access. <strong>Instance variables must be private</strong>.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💻 Component Example</h3>
            <div className="bg-slate-900 rounded-lg p-5 font-mono text-sm leading-relaxed overflow-x-auto">
              <p className="text-blue-400">public class <span className="text-yellow-200">Student</span> &#123;</p>
              <div className="pl-4">
                <p className="text-slate-500">// 1. Instance Variables (State)</p>
                <p><span className="text-purple-400">private</span> String name;</p>
                <p><span className="text-purple-400">private</span> <span className="text-orange-300">double</span> gpa;</p>
                <br />
                <p className="text-slate-500">// 2. Constructors</p>
                <p><span className="text-purple-400">public</span> <span className="text-yellow-200">Student</span>(String n) &#123; name = n; gpa = 0.0; &#125;</p>
                <br />
                <p className="text-slate-500">// 3. Methods (Behaviors)</p>
                <p><span className="text-purple-400">public void</span> <span className="text-emerald-400">study</span>(<span className="text-orange-300">double</span> hours) &#123; ... &#125;</p>
              </div>
              <p className="text-blue-400">&#125;</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Constructors",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏗️ Constructor Rules</h3>
            <ul className="list-decimal pl-6 space-y-2 text-slate-700 mb-4">
              <li><strong>Name:</strong> Must match the class name exactly.</li>
              <li><strong>No Return Type:</strong> Not even <code>void</code>.</li>
              <li><strong>Timing:</strong> Called only once per object via the <code>new</code> keyword.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 Overloading and Defaults</h3>
            <div className="space-y-4">
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">Overloading</h4>
                <p className="text-sm text-slate-700 mt-1">Multiple constructors allowed if <strong>signatures</strong> (parameter lists) differ.</p>
                <div className="bg-slate-800 text-[11px] p-2 rounded mt-2 text-indigo-200 font-mono">
                  public Student(String n, int id) &#123; ... &#125; <br />
                  public Student(String n) &#123; ... &#125;
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">The "No-Argument" Constructor</h4>
                <p className="text-sm text-slate-700 mt-1">If no constructor is written, Java provides a hidden default:</p>
                <ul className="grid grid-cols-2 gap-2 mt-2 text-xs font-mono">
                  <li className="bg-white p-1 rounded">int → 0</li>
                  <li className="bg-white p-1 rounded">double → 0.0</li>
                  <li className="bg-white p-1 rounded">boolean → false</li>
                  <li className="bg-white p-1 rounded">Object → null</li>
                </ul>
                <p className="text-xs text-red-600 mt-2 italic">Note: If you write ANY constructor, the default no-arg constructor is lost.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Methods: How to Write Them",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛠️ Method Mechanics</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Method Headers</h4>
                <p className="text-sm text-slate-700 font-mono mt-1">access_modifier return_type name(parameters)</p>
                <p className="text-xs text-slate-600 mt-1 italic">Example: <code>public double calculateAvg(int a, int b)</code></p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <KeyTerm term="Accessor (Getter)" definition="Returns the value of a private instance variable." />
                <KeyTerm term="Mutator (Setter)" definition="Modifies the value of a private instance variable." />
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">The return Keyword</h4>
                <ul className="list-disc pl-5 text-sm text-slate-700 mt-1">
                  <li>Stops execution immediately.</li>
                  <li>Passes a value back to the caller.</li>
                  <li>Type must match the method header's return type.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📖 Documentation</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Precondition" 
                definition="What must be true BEFORE the method is called (e.g., parameter must be positive)."
              />
              <KeyTerm 
                term="Postcondition" 
                definition="What is guaranteed to be true AFTER the method finishes (e.g., returns square root)."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-6",
      title: "Topic 3.6: Passing and Returning References",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧠 Memory and Parameters</h3>
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">Pass-by-Value (Primitives)</h4>
                <p className="text-sm text-slate-700 mt-1">Java passes a <strong>copy</strong> of the value. Changes inside the method <strong>do not</strong> affect the original variable.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">Pass-by-Value (Objects/References)</h4>
                <p className="text-sm text-slate-700 mt-1">Java passes a <strong>copy of the reference</strong> (memory address).</p>
                <div className="mt-2 space-y-2">
                  <div className="bg-white/60 p-2 rounded">
                    <p className="text-xs font-bold text-slate-800">Scenario A (Mutation):</p>
                    <p className="text-xs text-slate-600 italic">Calling <code>obj.setName("Bob")</code> CHANGES the original object.</p>
                  </div>
                  <div className="bg-white/60 p-2 rounded">
                    <p className="text-xs font-bold text-slate-800">Scenario B (Reassignment):</p>
                    <p className="text-xs text-slate-600 italic">Executing <code>obj = new Student()</code> DOES NOT change the original outside reference.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-7",
      title: "Topic 3.7: Class Variables and Methods (static)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📌 The static Keyword</h3>
            <p className="text-slate-700 mb-4">Means the member belongs to the <strong>Class</strong>, not a specific object.</p>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Static Variables</h4>
                <p className="text-sm text-slate-700 mt-1">Shared by <strong>all</strong> instances. Used for global counters (e.g., <code>studentCount</code>).</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">Static Methods</h4>
                <p className="text-sm text-slate-700 mt-1">Called via <code>ClassName.methodName()</code> (e.g., <code>Math.sqrt()</code>).</p>
                <p className="text-xs text-red-700 mt-2 font-bold">⚠️ RESTRICTION: Static methods CANNOT access instance variables or methods directly.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-8",
      title: "Topic 3.8: Scope and Access",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📍 Scope of Variables</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Local Variables</h4>
                <p className="text-xs text-slate-600 mt-1">Inside methods/constructors. Created at call, destroyed at end. <strong>No default values.</strong></p>
              </div>
              <div className="p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Instance Variables</h4>
                <p className="text-xs text-slate-600 mt-1">Inside class, outside methods. Life matches the object. Visible to all class methods.</p>
              </div>
            </div>
            <div className="mt-4 bg-red-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-bold text-red-900 text-sm">Shadowing</h4>
              <p className="text-xs text-slate-700 mt-1">When local variables share names with instance variables. Use <code>this</code> to resolve.</p>
              <code className="block mt-2 text-[11px] font-mono text-slate-800">this.name = name; // sets instance var to local param</code>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-9",
      title: "Topic 3.9: this Keyword",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🫵 Use Cases for this</h3>
            <p className="text-slate-700 mb-4">Reference to the <strong>current object</strong>.</p>
            <ul className="list-disc pl-6 space-y-3 text-sm text-slate-700">
              <li><strong>Differentiating Variables:</strong> Solving shadowing in constructors/setters.</li>
              <li><strong>Passing the Object:</strong> Using <code>other.process(this)</code> to pass the current instance.</li>
              <li><strong>Restriction:</strong> Cannot be used in <code>static</code> contexts.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "unit-3-exam-summary",
      title: "⭐ Summary for AP Exam Success",
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-yellow-50 to-amber-100 p-6 rounded-xl border border-amber-200">
            <h4 className="font-bold text-amber-900 mb-3">Unit 3 Cheat Sheet</h4>
            <div className="space-y-3 text-sm text-slate-800">
              <p>✅ <strong>Encapsulation:</strong> Check for <code>private</code> instance variables.</p>
              <p>✅ <strong>Constructors:</strong> Remember default no-arg vanishes if any custom constructor exists.</p>
              <p>✅ <strong>Static vs Instance:</strong> Static = Class; Instance = Object. Static methods can't see instance fields.</p>
              <p>✅ <strong>Reference Confusion:</strong> Tracing parameters? Mutation sticks, reassignment doesn't.</p>
            </div>
          </div>
        </div>
      )
    }
  ]
};

export default csaUnit3Notes;